import { useState, useEffect, useCallback, useRef } from 'react';
import { Header } from '@components/Header';
import { Sidebar } from '@components/Sidebar';
import { ChatArea } from '@components/ChatArea';
import { DocumentViewer } from '@components/DocumentViewer';
import { useCompactMode } from '@/hooks/useCompactMode';
import { MpepViewer } from '@components/MpepViewer';
import { CfrViewer } from '@components/CfrViewer';
import { UscViewer } from '@components/UscViewer';
import { DocumentsPage } from '@components/DocumentsPage';
import { SessionsPage } from '@components/SessionsPage';
import { ExaminationPage } from '@components/ExaminationPage';
import { ResourcesPage } from '@components/ResourcesPage';
import { TasksPage } from '@components/TasksPage';
import { USPTODataPage } from '@components/USPTODataPage';
import { DraftsPage } from '@components/DraftsPage';
import { USApplicationDisplay } from '@components/uspto/USApplicationDisplay';
import { Notifications } from '@components/Notifications';
import { DocumentOnboarding } from '@components/DocumentOnboarding';
import { BatchDocumentOnboarding } from '@components/BatchDocumentOnboarding';
import { FileUploadModal } from '@components/FileUploadModal';
import { SaveSessionModal } from '@components/SaveSessionModal';
import { NewSessionWarningModal } from '@components/NewSessionWarningModal';
import { OnboardingWizard } from '@components/OnboardingWizard';
import { useUIStore, useDocumentStore, useSessionStore, useNotificationStore, useUserStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { useAuth } from '@context/AuthContext';
import { patentApi, resourceApi, sessionApi, documentApi } from '@/services/api';
import { addLoadingDocument } from '@/utils/loadingDocuments';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Button } from '@components/ui/button';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, BookOpen, X, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PageType = 'chat' | 'documents' | 'uspto_data' | 'examination' | 'resources' | 'tasks' | 'sessions' | 'drafts';

interface CfrSection {
  number: string;
  title?: string;
}

interface CfrSubpart {
  id: string;
  title: string;
  sections: CfrSection[];
}

interface CfrPart {
  id: string;
  number: string;
  title: string;
  sections: CfrSection[];
  subparts: CfrSubpart[];
}

const normalizeCfrStructure = (rawStructure: any): CfrPart[] => {
  if (!Array.isArray(rawStructure)) return [];
  const usptoChapter = rawStructure.find((chapter: any) => chapter.number === 'I');
  if (!usptoChapter) return [];

  const parts: CfrPart[] = [];

  usptoChapter.children?.forEach((subchapter: any) => {
    subchapter.children?.forEach((part: any) => {
      if (part.type !== 'part') return;
      const partNumber = part.number || part.name || '';
      const cleanedTitle =
        part.name?.replace(/^PART\s+\d+—?\s*/i, '').trim() ||
        part.name ||
        `Part ${partNumber}`;

      const directSections: CfrSection[] = [];
      const subparts: CfrSubpart[] = [];

      part.children?.forEach((child: any) => {
        if (child.type === 'section') {
          const sectionNumber = (child.number || '').replace(/§\s*/, '').trim();
          if (sectionNumber) {
            directSections.push({
              number: sectionNumber,
              title: child.name,
            });
          }
        } else if (child.type === 'subpart') {
          const subpartSections: CfrSection[] =
            child.children
              ?.filter((subChild: any) => subChild.type === 'section')
              .map((sectionNode: any) => ({
                number: (sectionNode.number || '').replace(/§\s*/, '').trim(),
                title: sectionNode.name,
              }))
              .filter((sec: CfrSection) => !!sec.number) || [];

          if (subpartSections.length > 0) {
            subparts.push({
              id: child.number || child.name,
              title:
                child.name?.replace(/^Subpart\s+[A-Z]+\s*—?\s*/i, '').trim() ||
                child.name ||
                (child.number ? `Subpart ${child.number}` : 'Subpart'),
              sections: subpartSections,
            });
          }
        }
      });

      parts.push({
        id: partNumber,
        number: partNumber,
        title: cleanedTitle,
        sections: directSections,
        subparts,
      });
    });
  });

  return parts.sort((a, b) => parseFloat(a.number) - parseFloat(b.number));
};

export default function Main() {
  const [activePage, setActivePage] = useState<PageType>('chat');
  const [selectedDocument, setSelectedDocument] = useState<any>(null);

  // Multi-document support for chat page
  const [selectedChatDocuments, setSelectedChatDocuments] = useState<Array<{
    id: string;
    doc: any;
    type: string;
  }>>([]);
  const [chatDocumentArrangement, setChatDocumentArrangement] = useState<'horizontal' | 'vertical' | 'grid'>('horizontal');

  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');
  const [showDocumentOnboarding, setShowDocumentOnboarding] = useState(false);
  const [showBatchDocumentOnboarding, setShowBatchDocumentOnboarding] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveAsMode, setSaveAsMode] = useState(false);
  const [showNewSessionWarning, setShowNewSessionWarning] = useState(false);
  const [sessionContext, setSessionContext] = useState<any>(null);
  const [documentViewerWidth, setDocumentViewerWidth] = useState(activePage === 'tasks' ? 400 : 600); // px
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mpepViewerState, setMpepViewerState] = useState<{
    section: string;
    title?: string;
    loading: boolean;
    content: any;
    error?: string;
  } | null>(null);
  const [mpepStructure, setMpepStructure] = useState<any>(null);
  const [mpepStructureLoading, setMpepStructureLoading] = useState(false);
  const [cfrViewerState, setCfrViewerState] = useState<{
    section: string;
    title?: string;
    loading: boolean;
    content: any;
    error?: string;
  } | null>(null);
  const [cfrStructure, setCfrStructure] = useState<CfrPart[] | null>(null);
  const [cfrStructureLoading, setCfrStructureLoading] = useState(false);
  const [uscViewerState, setUscViewerState] = useState<{
    section: string;
    title?: string;
    loading: boolean;
    content: any;
    error?: string;
  } | null>(null);
  const [uscStructure, setUscStructure] = useState<any>(null);
  const [uscStructureLoading, setUscStructureLoading] = useState(false);
  const [uscFullInfo, setUscFullInfo] = useState<any>(null); // Store full USC data including content
  const [applicationViewerState, setApplicationViewerState] = useState<{
    applicationData: any;
    pdfUrl?: string | null;
    pdfLoading?: boolean;
  } | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);
  const [pdfScale, setPdfScale] = useState<number>(1.0);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [examinationApplicationNumber, setExaminationApplicationNumber] = useState<string | undefined>(undefined);
  const { user, loading: authLoading, logout } = useAuth();
  const { compactMode } = useCompactMode();

  // Helper functions for managing chat documents
  const addChatDocument = (doc: any, type: string) => {
    const id = `${type}-${doc.file_id || doc.docId || doc.resource_id || doc.publication_number || doc.patent_number || Math.random()}`;

    // Check if this document is already selected
    const alreadySelected = selectedChatDocuments.some(chatDoc => chatDoc.id === id);
    if (alreadySelected) {
      return; // Don't add duplicates
    }

    setSelectedChatDocuments(prev => [...prev, { id, doc, type }]);
    setShowDocWindow(true);
  };

  const removeChatDocument = (id: string) => {
    setSelectedChatDocuments(prev => {
      const newDocs = prev.filter(chatDoc => chatDoc.id !== id);
      // If no documents left, close the document window
      if (newDocs.length === 0) {
        setShowDocWindow(false);
      }
      return newDocs;
    });
  };

  const removeAllChatDocuments = () => {
    setSelectedChatDocuments([]);
    setShowDocWindow(false);
  };

  const hasCompletedOnboarding = useUserStore(state =>
    state.userSettings.hasCompletedOnboarding as boolean | undefined
  );
  const showWizardOnStartup = useUserStore(state =>
    state.userSettings.showWizardOnStartup !== false // Default to true if not set
  );

  const showDocWindow = useDocumentStore(state => state.showDocWindow);
  const setShowDocWindow = useDocumentStore(state => state.setShowDocWindow);

  const chatMessages = useSessionStore(state => state.chatMessages);
  const sessionId = useSessionStore(state => state.sessionId);
  const sessionName = useSessionStore(state => state.sessionName);
  const sessionHasBeenSaved = useSessionStore(state => state.sessionHasBeenSaved);
  const savedFolderForSession = useSessionStore(state => state.savedFolderForSession);
  const sessionDocuments = useSessionStore(state => state.sessionDocuments);
  const sessionResources = useSessionStore(state => state.sessionResources);
  const setSessionApplications = useSessionStore(state => state.setSessionApplications);
  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const resetSession = useSessionStore(state => state.resetSession);
  const setSessionDocuments = useSessionStore(state => state.setSessionDocuments);
  const setSessionResources = useSessionStore(state => state.setSessionResources);
  const setSessionHasBeenSaved = useSessionStore(state => state.setSessionHasBeenSaved);
  const addNotification = useNotificationStore(state => state.addNotification);
  const startPolling = useJobPollingStore(state => state.startPolling);

  // Fetch session applications on mount
  useEffect(() => {
    const loadSessionApplications = async () => {
      try {
        // Override cache on page load to get fresh data
        const applications = await patentApi.getSessionApplications(true);
        setSessionApplications(applications);
      } catch (error) {
        console.error('Failed to load session applications:', error);
      }
    };

    loadSessionApplications();
  }, [setSessionApplications]);

  // Load user settings from backend on mount
  useEffect(() => {
    const { loadUserSettingsFromBackend } = useUserStore.getState();
    console.log('[Main] Loading user settings from backend on mount');
    loadUserSettingsFromBackend();
  }, []);

  useEffect(() => {
    const handleSessionExpired = (event: any) => {
      const message = event.detail?.message || 'Your session has expired. Please login again.';
      setSessionExpiredMessage(message);
      setShowSessionExpiredModal(true);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    const loadSessionContext = () => {
      const storedData = localStorage.getItem('session_context');
      if (storedData && storedData !== "undefined") {
        try {
          const parsedData = JSON.parse(storedData);
          setSessionContext(parsedData);
        } catch (error) {
          console.error('Failed to parse session context:', error);
          setSessionContext(null);
        }
      } else {
        setSessionContext(null);
      }
    };

    loadSessionContext();

    // Listen for context updates
    const handleStorageUpdate = () => loadSessionContext();
    window.addEventListener('storage-update', handleStorageUpdate);
    window.addEventListener('storage', loadSessionContext);

    return () => {
      window.removeEventListener('storage-update', handleStorageUpdate);
      window.removeEventListener('storage', loadSessionContext);
    };
  }, []);

  // Check if user needs onboarding on first login
  useEffect(() => {
    if (user && !authLoading && (showWizardOnStartup || hasCompletedOnboarding === false)) {
      // Small delay to let the UI settle before showing wizard
      const timer = setTimeout(() => {
        setShowOnboardingWizard(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    // Note: showWizardOnStartup is intentionally NOT in the dependency array
    // We only want to check it on initial login, not when the setting is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, hasCompletedOnboarding]);

  // Listen for manual wizard reopen from menu
  useEffect(() => {
    const handleReopenWizard = () => setShowOnboardingWizard(true);
    window.addEventListener('open-onboarding-wizard', handleReopenWizard);
    return () => window.removeEventListener('open-onboarding-wizard', handleReopenWizard);
  }, []);

  // Clear examination application number when navigating away from examination page
  useEffect(() => {
    if (activePage !== 'examination') {
      setExaminationApplicationNumber(undefined);
    }
  }, [activePage]);

  // Update document viewer width based on active page
  useEffect(() => {
    setDocumentViewerWidth(activePage === 'tasks' ? 400 : 600);
  }, [activePage]);

  // Global job completion listener - persists across page navigation
  useEffect(() => {
    const handleJobComplete = async (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[Main] Job completion event received:', event.detail);
      // Format resource type name: convert underscores to spaces and capitalize each word
      const resourceTypeName = docType
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (success) {
        addNotification({
          type: 'success',
          title: `${resourceTypeName} Ready`,
          message: `${docId} has been loaded and is ready to use.`,
          duration: 5000,
        });

        // If an application was loaded, refresh session applications
        if (docType === 'application') {
          try {
            const applications = await patentApi.getSessionApplications(true);
            setSessionApplications(applications);
            console.log('[Main] Session applications refreshed after application load');
          } catch (err) {
            console.error('Failed to refresh session applications:', err);
          }
        }

        // Trigger session context reload to update UI
        window.dispatchEvent(new Event('storage-update'));
      } else {
        let errorMessage = `Failed to load ${docId}. Please try again.`;

        if (error === '404') {
          errorMessage = `${docId} not found. This may be an older document. Please verify the number is correct.`;
        } else if (error === 'timeout') {
          addNotification({
            type: 'warning',
            title: 'Loading Timeout',
            message: `${docId} loading timed out. It may still complete in the background.`,
            duration: 5000,
          });
          return;
        } else if (error === 'polling-error') {
          errorMessage = `Error checking status for ${docId}.`;
        }

        addNotification({
          type: 'error',
          title: `${resourceTypeName} Load Failed`,
          message: errorMessage,
          duration: 8000,
        });
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [addNotification, setSessionApplications]);

  const handleSessionExpiredConfirm = () => {
    setShowSessionExpiredModal(false);
    logout();
  };

  // Session control handlers
  const handleNewSession = () => {
    // Always show modal to let user choose document handling
    setShowNewSessionWarning(true);
  };

  const handleSave = async () => {
    if (sessionHasBeenSaved && sessionId && sessionName) {
      // Session has been saved before - save directly without modal
      try {
        // Update session context to include current active drafts
        const sessionContextStr = localStorage.getItem('session_context');
        const sessionContext = sessionContextStr ? JSON.parse(sessionContextStr) : {};
        const activeDraftsStr = localStorage.getItem('active_drafts');
        const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
        sessionContext.current_drafts = activeDrafts;
        localStorage.setItem('session_context', JSON.stringify(sessionContext));
        console.log('[Main.handleSave] Saving session with drafts:', activeDrafts);

        await sessionApi.saveSession(
          sessionId,
          sessionName,
          chatMessages,
          savedFolderForSession
        );
        setSessionHasBeenSaved(true);

        // Dispatch event to update session list
        window.dispatchEvent(new Event('sessions-updated'));
      } catch (error) {
        console.error('Failed to save session:', error);
        // Could add a toast notification here
      }
    } else {
      // First time save - show modal for name input
      setSaveAsMode(false);
      setShowSaveModal(true);
    }
  };

  const handleSaveAs = () => {
    setSaveAsMode(true);
    setShowSaveModal(true);
  };

  // Keyboard shortcut for Ctrl+S to save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+S (or Cmd+S on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's default save behavior
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sessionHasBeenSaved, sessionId, sessionName, chatMessages, savedFolderForSession]);

  const handleNewSessionConfirm = (clearDocuments: boolean) => {
    // User confirmed to start new session
    if (clearDocuments) {
      // Clear all documents
      setSessionDocuments([]);
      setSessionResources([]);
      setSessionApplications([]);

      // Also clear session_context from localStorage
      const context = {
        current_patents: [],
        current_applications: [],
        current_publications: [],
        current_foreign_patents: [],
        current_drafts: [],
        resource_ids: [],
        resource_names: [],
        new_resource_ids: [],
        new_resource_names: [],
        mpep_sections: [],
        cfr_sections: [],
        usc_sections: [],
        fed_reg_sections: [],
        pros_docs: [],
        patent_numbers_dict_arr: [],
        pros_doc_names: [],
        extracted_resources: [],
        current_patent: null,
        current_publication: null,
        current_application: null,
        last_context_type: null,
        last_context_number: null,
      };

      // Clear active drafts
      localStorage.setItem('active_drafts', JSON.stringify([]));
      localStorage.setItem('session_context', JSON.stringify(context));

      // Also clear separate localStorage keys
      localStorage.removeItem('new_resource_ids');
      localStorage.removeItem('new_resource_names');
      localStorage.removeItem('patents');
    }

    // Reset session and reload
    resetSession();
    window.location.reload();
  };

  const handleNewSessionSaveFirst = () => {
    setSaveAsMode(false);
    setShowSaveModal(true);
  };

  const handlePasteQuestionFromWizard = (question: string) => {
    // Paste the question into chat input by dispatching a custom event
    // The ChatArea component will listen for this event
    window.dispatchEvent(new CustomEvent('paste-question', { detail: { question } }));

    // Switch to chat view so user can see the input
    setActivePage('chat');
  };

  const handleOpenDocumentModalFromWizard = () => {
    // Open the document onboarding modal
    setShowDocumentOnboarding(true);
  };

  const handleOpenUploadModalFromWizard = () => {
    // Open the file upload modal
    setShowFileUploadModal(true);
  };

  const handleNavigateToPageFromWizard = (page: 'uspto_data' | 'resources' | 'examination' | 'tasks', applicationNumber?: string, taskId?: string) => {
    console.log('[Main] Navigate to page:', page, 'with application:', applicationNumber, 'task:', taskId);

    // Navigate to the specified page first
    setActivePage(page);

    // If navigating to examination with an application number, dispatch event after navigation
    if (page === 'examination' && applicationNumber) {
      console.log('[Main] Dispatching select-examination-application event for:', applicationNumber);
      // Use setTimeout to ensure the page has rendered before dispatching the event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('select-examination-application', {
          detail: { applicationNumber }
        }));
      }, 100);
    }

    // If navigating to tasks with a taskId, dispatch event to select that task
    if (page === 'tasks' && taskId) {
      console.log('[Main] Dispatching select-task event for:', taskId);
      // Use setTimeout to ensure the page has rendered before dispatching the event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('select-task', {
          detail: { taskId }
        }));
      }, 100);
    }
  };

  const handleAddApplicationFromWizard = async (appNumber: string): Promise<void> => {
    // Clean the application number
    let cleanedId = appNumber
      .replace(/,/g, '')
      .replace(/\//g, '')
      .replace(/^US/i, '')
      .replace(/[A-Z]\d*$/i, '');

    // Validate format (8 digits)
    const pattern = /^\d{8}$/;
    if (!pattern.test(cleanedId)) {
      throw new Error('Application number must be 8 digits (e.g., 16123456 or 16/123,456)');
    }

    try {
      // Call API to fetch and load the application
      const response = await documentApi.fetchAndLoadDocument('application', cleanedId);

      if (response?.job_id) {
        // Add to session context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
        context.current_applications = [...(context.current_applications || []), cleanedId];

        // Update context immediately so it shows in UI
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        // Show processing notification
        addNotification({
          type: 'info',
          title: 'Loading Application',
          message: `Processing ${cleanedId}...`,
          duration: 60000,
        });

        // Add to loading documents tracking
        addLoadingDocument(cleanedId, 'Application', cleanedId);

        // Start polling for job status
        startPolling(response.job_id, 'application', cleanedId);
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (error) {
      console.error(`Failed to load application ${cleanedId}:`, error);
      throw error;
    }
  };

  const handlePageChange = (page: PageType) => {
    setActivePage(page);
  };

  const extractMpepSection = (doc: any): { section: string | null; title?: string } => {
    if (!doc) return { section: null };
    if (doc.section) return { section: String(doc.section), title: doc.title };

    const possibleSources = [
      doc.title,
      doc.file_name,
      doc.resource_id,
      doc.doc_id,
      doc.id,
    ];

    for (const source of possibleSources) {
      if (typeof source !== 'string') continue;
      const match = source.match(/MPEP\s*§?\s*([0-9.]+)/i) || source.match(/([0-9.]+)$/);
      if (match) {
        return { section: match[1], title: doc.title || `MPEP §${match[1]}` };
      }
    }
    return { section: null, title: doc.title };
  };

  const extractCfrSection = (doc: any): { section: string | null; title?: string } => {
    if (!doc) return { section: null };
    const candidates = [
      doc.section,
      doc.doc_id,
      doc.resource_id,
      typeof doc.title === 'string' ? doc.title : null,
    ];

    for (const candidate of candidates) {
      if (typeof candidate !== 'string') continue;
      const match = candidate.match(/(\d+\.\d+[a-z0-9()\-.]*)/i);
      if (match) {
        return { section: match[1], title: doc.title };
      }
    }

    return { section: null, title: doc.title };
  };

  const ensureMpepStructure = async () => {
    if (mpepStructure || mpepStructureLoading) return;
    try {
      setMpepStructureLoading(true);
      const structure = await resourceApi.fetchMpepInfo();
      setMpepStructure(structure);
    } catch (error) {
      console.error('Failed to load MPEP structure:', error);
    } finally {
      setMpepStructureLoading(false);
    }
  };

  const ensureCfrStructure = async () => {
    if (cfrStructure || cfrStructureLoading) return;
    setCfrStructureLoading(true);
    try {
      const response = await fetch('/cfr_info.json');
      if (!response.ok) {
        throw new Error('Failed to load CFR structure');
      }
      const data = await response.json();
      setCfrStructure(normalizeCfrStructure(data));
    } catch (error) {
      console.error('Failed to load CFR structure:', error);
    } finally {
      setCfrStructureLoading(false);
    }
  };

  const loadMpepSection = async (section: string, title?: string) => {
    const normalizedSection = section.replace(/[^0-9.]/g, '');
    if (!normalizedSection) {
      setMpepViewerState({
        section,
        title,
        loading: false,
        content: null,
        error: 'Invalid section number.',
      });
      return;
    }

    setMpepViewerState({
      section: normalizedSection,
      title,
      loading: true,
      content: null,
      error: undefined,
    });

    try {
      const content = await resourceApi.fetchMpepContent(normalizedSection);
      if (!content) {
        setMpepViewerState(prev => prev ? { ...prev, loading: false, error: 'No content returned for this section.' } : prev);
        return;
      }
      setMpepViewerState(prev => prev ? { ...prev, loading: false, content, error: undefined } : prev);
    } catch (error) {
      console.error('Failed to load MPEP section:', error);
      setMpepViewerState(prev => prev ? { ...prev, loading: false, error: 'Failed to load section. Please try again.' } : prev);
    }
  };

  const loadCfrSection = async (section: string, title?: string) => {
    const normalizedSection = section.replace(/[^0-9.\w()-]/g, '');
    if (!normalizedSection) {
      setCfrViewerState({
        section,
        title,
        loading: false,
        content: null,
        error: 'Invalid section number.',
      });
      return;
    }

    setCfrViewerState({
      section: normalizedSection,
      title,
      loading: true,
      content: null,
      error: undefined,
    });

    try {
      const content = await resourceApi.fetchCfrContent(normalizedSection);
      if (!content) {
        setCfrViewerState((prev) =>
          prev ? { ...prev, loading: false, error: 'No content returned for this section.' } : prev
        );
        return;
      }
      setCfrViewerState((prev) =>
        prev ? { ...prev, loading: false, content, error: undefined } : prev
      );
    } catch (error) {
      console.error('Failed to load CFR section:', error);
      setCfrViewerState((prev) =>
        prev ? { ...prev, loading: false, error: 'Failed to load section. Please try again.' } : prev
      );
    }
  };

  const extractUscSection = (doc: any): { section: string | null; title?: string } => {
    if (!doc) return { section: null };

    // First try direct properties that should contain just the section number
    const directCandidates = [doc.section, doc.resource_id];
    for (const candidate of directCandidates) {
      if (typeof candidate === 'string') {
        const match = candidate.match(/(\d+[a-z]?)/i);
        if (match) {
          return { section: match[1], title: doc.title };
        }
      }
    }

    // Then try the title with a more specific pattern (e.g., "35 USC 112")
    if (typeof doc.title === 'string') {
      const uscMatch = doc.title.match(/(?:35\s+)?U\.?S\.?C\.?\s*§?\s*(\d+[a-z]?)/i);
      if (uscMatch) {
        return { section: uscMatch[1], title: doc.title };
      }
    }

    // Finally try other generic fields
    const genericCandidates = [doc.doc_id, doc.id];
    for (const candidate of genericCandidates) {
      if (typeof candidate === 'string') {
        const match = candidate.match(/(\d+[a-z]?)/i);
        if (match) {
          return { section: match[1], title: doc.title };
        }
      }
    }

    return { section: null, title: doc.title };
  };

  const ensureUscStructure = async () => {
    if ((uscStructure && uscFullInfo) || uscStructureLoading) return;
    setUscStructureLoading(true);
    try {
      const data = await resourceApi.fetchUscInfo();

      // Store the full USC info for content lookup
      setUscFullInfo(data);

      // Check if there's a separate structure property for navigation
      if (data && data.structure && Array.isArray(data.structure)) {
        setUscStructure(data.structure);
      } else if (data && data.usc_info) {
        // Build structure from available sections
        // Title 35 USC is organized into 5 parts
        const availableSections = Object.keys(data.usc_info)
          .map(k => parseInt(k, 10))
          .filter(n => !isNaN(n))
          .sort((a, b) => a - b);

        const structure = [
          {
            part_number: 'I',
            part_title: 'United States Patent and Trademark Office',
            sections: availableSections
              .filter(n => n >= 1 && n <= 14)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'II',
            part_title: 'Patentability of Inventions and Grant of Patents',
            sections: availableSections
              .filter(n => n >= 100 && n <= 212)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'III',
            part_title: 'Patents and Protection of Patent Rights',
            sections: availableSections
              .filter(n => n >= 251 && n <= 329)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'IV',
            part_title: 'Patent Cooperation Treaty',
            sections: availableSections
              .filter(n => n >= 351 && n <= 376)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          },
          {
            part_number: 'V',
            part_title: 'Hague Agreement - International Design Registration',
            sections: availableSections
              .filter(n => n >= 381 && n <= 390)
              .map(n => {
                const sectionData = data.usc_info[n];
                const title = sectionData?.header?.replace(/^§\d+\.\s*/, '') || '';
                return {
                  section_number: n.toString(),
                  section_title: title
                };
              })
          }
        ].filter(part => part.sections.length > 0); // Only include parts with available sections

        setUscStructure(structure);
      } else {
        console.warn('USC structure not in expected format:', data);
        setUscStructure([]);
      }
    } catch (error) {
      console.error('Failed to load USC structure:', error);
      setUscStructure([]);
      setUscFullInfo(null);
    } finally {
      setUscStructureLoading(false);
    }
  };

  const loadUscSection = async (section: string, title?: string) => {
    const normalizedSection = section.replace(/[^0-9a-z]/gi, '');
    if (!normalizedSection) {
      setUscViewerState({
        section,
        title,
        loading: false,
        content: null,
        error: 'Invalid section number.',
      });
      return;
    }

    setUscViewerState({
      section: normalizedSection,
      title,
      loading: true,
      content: null,
      error: undefined,
    });

    try {
      // USC content is embedded in the full info structure, not fetched separately
      const currentUscInfo = await resourceApi.fetchUscInfo();

      // Store it for future use
      if (!uscFullInfo) {
        setUscFullInfo(currentUscInfo);
      }

      // Extract section content from usc_info object
      // The keys are numeric (1, 2, 3, ..., 112, etc.)
      const numericSection = parseInt(normalizedSection, 10);
      let sectionData = null;

      if (!isNaN(numericSection)) {
        // Try numeric key first
        sectionData = currentUscInfo?.usc_info?.[numericSection];
      }

      // If not found, try string key
      if (!sectionData) {
        sectionData = currentUscInfo?.usc_info?.[normalizedSection];
      }

      if (!sectionData) {
        setUscViewerState({
          section: normalizedSection,
          title: title || `35 U.S.C. § ${normalizedSection}`,
          loading: false,
          content: null,
          error: `Section ${normalizedSection} not found in the database.`,
        });
        return;
      }

      // Format content similar to how ResourcesPage does it
      const content = {
        header: sectionData.header,
        content: sectionData.content,
        raw_html: sectionData.raw_html,
        text: sectionData.content || sectionData.raw_html
      };

      setUscViewerState({
        section: normalizedSection,
        title: title || `35 U.S.C. § ${normalizedSection}`,
        loading: false,
        content,
        error: undefined,
      });
    } catch (error) {
      console.error('Failed to load USC section:', error);
      setUscViewerState({
        section: normalizedSection,
        title: title || `35 U.S.C. § ${normalizedSection}`,
        loading: false,
        content: null,
        error: 'Failed to load section. Please try again.',
      });
    }
  };

  const handleDocumentSelect = useCallback((doc: any) => {
    console.log('Main handleDocumentSelect - received document:', doc);
    const docType = (doc?.type || doc?.file_type || '').toString().toLowerCase();
    if (docType === 'mpep' || (doc?.title && doc.title.toLowerCase().includes('mpep'))) {
      const { section, title } = extractMpepSection(doc);
      if (section) {
        ensureMpepStructure();
        setCfrViewerState(null);
        setUscViewerState(null);
        setApplicationViewerState(null);
        loadMpepSection(section, title);
        setShowDocWindow(false);
      } else {
        addNotification({
          type: 'error',
          title: 'Unable to open MPEP section',
          message: 'This entry does not include a recognizable section number.',
        });
      }
      return;
    }

    if (docType.includes('cfr') || (doc?.title && doc.title.toLowerCase().includes('cfr'))) {
      const { section, title } = extractCfrSection(doc);
      if (section) {
        ensureCfrStructure();
        setMpepViewerState(null);
        setUscViewerState(null);
        setApplicationViewerState(null);
        loadCfrSection(section, title);
        setShowDocWindow(false);
      } else {
        addNotification({
          type: 'error',
          title: 'Unable to open 37 CFR section',
          message: 'This entry does not include a recognizable section number.',
        });
      }
      return;
    }

    if (docType.includes('usc') || (doc?.title && doc.title.toLowerCase().includes('usc'))) {
      const { section, title } = extractUscSection(doc);
      if (section) {
        ensureUscStructure();
        setMpepViewerState(null);
        setCfrViewerState(null);
        setApplicationViewerState(null);
        loadUscSection(section, title);
        setShowDocWindow(false);
      } else {
        addNotification({
          type: 'error',
          title: 'Unable to open 35 USC section',
          message: 'This entry does not include a recognizable section number.',
        });
      }
      return;
    }

    // Handle applications - show USPTO Data
    if (docType === 'application') {
      setMpepViewerState(null);
      setCfrViewerState(null);
      setUscViewerState(null);
      setShowDocWindow(false);

      // Extract application number from resource_id or title
      let appNumber = doc.resource_id || doc.title || '';
      appNumber = appNumber.replace(/^application-/i, '').trim();

      // Find the application data from sessionApplications
      const sessionApp = sessionApplications?.find(app =>
        (app.application_id || app.application?.applicationNumberText) === appNumber
      );

      if (sessionApp && sessionApp.application) {
        // Map session application structure to USApplicationDisplay expected structure
        setApplicationViewerState({
          applicationData: {
            application_number: appNumber,
            patent_info: {
              applicationMetaData: sessionApp.application.applicationMetaData || {}
            },
            pgpubDocumentMetaData: sessionApp.application.pgpubDocumentMetaData,
            grantDocumentMetaData: sessionApp.application.grantDocumentMetaData,
          }
        });
      } else {
        // If not found in session, show minimal state
        setApplicationViewerState({
          applicationData: {
            application_number: appNumber,
            patent_info: {
              applicationMetaData: {}
            }
          }
        });

        addNotification({
          type: 'info',
          title: 'Application Not Loaded',
          message: `Application ${appNumber} is not fully loaded. Try adding it from USPTO Data page.`,
        });
      }

      return;
    }

    setMpepViewerState(null);
    setCfrViewerState(null);
    setUscViewerState(null);
    setApplicationViewerState(null);

    // On chat page, support multiple documents
    if (activePage === 'chat') {
      const docType = (doc?.type || doc?.file_type || '').toString().toLowerCase();
      addChatDocument(doc, docType || 'unknown');
    } else {
      // On other pages, use single document viewer
      setSelectedDocument(doc);
      setShowDocWindow(true);
    }
  }, []); // Empty deps array to prevent recreation

  const handleCloseDocument = () => {
    if (activePage === 'chat') {
      removeAllChatDocuments();
    } else {
      setShowDocWindow(false);
      setSelectedDocument(null);
    }
  };

  // Close document window when navigating to examination page with a document
  useEffect(() => {
    if (activePage === 'examination' && selectedDocument && showDocWindow) {
      setShowDocWindow(false);
    }
  }, [activePage, selectedDocument, showDocWindow]);

  const handleCloseMpepViewer = () => {
    setMpepViewerState(null);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  const handleCloseCfrViewer = () => {
    setCfrViewerState(null);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  const handleCloseUscViewer = () => {
    setUscViewerState(null);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  const handleCloseApplicationViewer = () => {
    setApplicationViewerState(null);
    setPdfNumPages(0);
    setPdfScale(1.0);
    // If on chat page with selected documents, restore document viewer
    if (activePage === 'chat' && selectedChatDocuments.length > 0) {
      setShowDocWindow(true);
    }
  };

  const handleProsecutionDocClick = async (doc: any) => {
    // For prosecution documents, fetch and display the PDF in the sidepane
    try {
      const appNumber = applicationViewerState?.applicationData?.application_number;
      if (!appNumber) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Application number not found',
        });
        return;
      }

      // Set loading state
      setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: true, pdfUrl: null } : null);
      setPdfNumPages(0);

      // Fetch the PDF URL using the patent API
      const pdfUrl = await patentApi.downloadProsecutionDocPdf(appNumber, doc.docId);

      if (pdfUrl) {
        // Set the PDF URL to display in the viewer
        setApplicationViewerState(prev => prev ? { ...prev, pdfUrl, pdfLoading: false } : null);
        addNotification({
          type: 'success',
          title: 'Document Loaded',
          message: `${doc.description} is ready`,
        });
      } else {
        setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Unable to retrieve prosecution document',
        });
      }
    } catch (error) {
      console.error('Failed to load prosecution document:', error);
      setApplicationViewerState(prev => prev ? { ...prev, pdfLoading: false } : null);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load prosecution document',
      });
    }
  };

  const handleNavigateToMpepSection = (sectionNumber: string) => {
    ensureMpepStructure();
    loadMpepSection(sectionNumber, `MPEP §${sectionNumber}`);
  };

  const handleNavigateToCfrSection = (sectionNumber: string) => {
    ensureCfrStructure();
    loadCfrSection(sectionNumber, `37 C.F.R. § ${sectionNumber}`);
  };

  const handleNavigateToUscSection = (sectionNumber: string) => {
    ensureUscStructure();
    loadUscSection(sectionNumber, `35 U.S.C. § ${sectionNumber}`);
  };

  const handleViewApplicationFromDocument = (applicationNumber: string) => {
    // Find the application in sessionApplications
    const sessionApp = sessionApplications?.find(app =>
      (app.application_id || app.application?.applicationNumberText) === applicationNumber
    );

    // Close other viewers
    setMpepViewerState(null);
    setCfrViewerState(null);

    if (sessionApp && sessionApp.application) {
      // Map session application structure to USApplicationDisplay expected structure
      setApplicationViewerState({
        applicationData: {
          application_number: applicationNumber,
          patent_info: {
            applicationMetaData: sessionApp.application.applicationMetaData || {}
          },
          pgpubDocumentMetaData: sessionApp.application.pgpubDocumentMetaData,
          grantDocumentMetaData: sessionApp.application.grantDocumentMetaData,
        }
      });
    } else {
      // If not found in session, show minimal state
      setApplicationViewerState({
        applicationData: {
          application_number: applicationNumber,
          patent_info: {
            applicationMetaData: {}
          }
        }
      });

      addNotification({
        type: 'info',
        title: 'Application Not Loaded',
        message: `Application ${applicationNumber} is not fully loaded. Try adding it from USPTO Data page.`,
      });
    }
  };

  // Handle document viewer resize
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      // Calculate new width from right edge
      const newWidth = window.innerWidth - e.clientX;

      // Constrain width between 400px and (100vw - 700px)
      const minWidth = 400;
      const minChatWidth = 700;
      const maxWidth = window.innerWidth - minChatWidth;
      const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      setDocumentViewerWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Calculate button visibility
  const getActiveDocumentCount = () => {
    try {
      const sessionContextStr = localStorage.getItem('session_context');
      if (!sessionContextStr || sessionContextStr === 'undefined') return 0;

      const sessionContext = JSON.parse(sessionContextStr);
      let count = 0;

      // Count current patents
      if (sessionContext.current_patents && Array.isArray(sessionContext.current_patents)) {
        count += sessionContext.current_patents.length;
      }

      // Count current publications
      if (sessionContext.current_publications && Array.isArray(sessionContext.current_publications)) {
        count += sessionContext.current_publications.length;
      }

      // Count current applications
      if (sessionContext.current_applications && Array.isArray(sessionContext.current_applications)) {
        count += sessionContext.current_applications.length;
      }

      // Count current foreign patents
      if (sessionContext.current_foreign_patents && Array.isArray(sessionContext.current_foreign_patents)) {
        count += sessionContext.current_foreign_patents.length;
      }

      // Count other document types
      if (sessionContext.cfr_sections && Array.isArray(sessionContext.cfr_sections)) {
        count += sessionContext.cfr_sections.length;
      }

      if (sessionContext.mpep_sections && Array.isArray(sessionContext.mpep_sections)) {
        count += sessionContext.mpep_sections.length;
      }

      if (sessionContext.resource_ids && Array.isArray(sessionContext.resource_ids)) {
        count += sessionContext.resource_ids.length;
      }

      return count;
    } catch (error) {
      console.error('Error parsing session_context:', error);
      return 0;
    }
  };

  const hasDocuments = getActiveDocumentCount() > 0;
  const hasContent = chatMessages.length > 1 || hasDocuments;
  const showSaveButton = hasContent;
  const showSaveAsButton = sessionId && hasContent;
  const hasUnsavedChanges = chatMessages.length > 1 && !sessionHasBeenSaved;

  return (
    <div className={`flex h-screen bg-gray-50 ${compactMode ? 'compact-mode' : ''} relative`}>
      {/* Sidebar - Only show on chat page */}
      {activePage === 'chat' && (
        <>
          <Sidebar
            activePage={activePage}
            onPageChange={handlePageChange}
            onDocumentSelect={handleDocumentSelect}
            onAddDocuments={() => setShowDocumentOnboarding(true)}
            onNewSession={handleNewSession}
            onSaveSession={handleSave}
            onSaveAsSession={handleSaveAs}
            showSaveButton={showSaveButton}
            showSaveAsButton={showSaveAsButton}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />

          {/* Sidebar Collapse Toggle - Positioned on border */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-200 rounded-full h-6 w-6 p-0 shadow-sm hover:shadow-md transition-all"
            style={{
              left: sidebarCollapsed ? '-4px' : compactMode ? '216px' : '256px'
            }}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </Button>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          activePage={activePage}
          onPageChange={handlePageChange}
        />

        <div className="flex-1 flex overflow-hidden">
          {/* Main Content - Changes based on active page */}
          <div className={`${showDocWindow && activePage === 'documents' ? '' : 'flex-1 min-w-0'} flex flex-col ${showDocWindow && activePage === 'chat' ? 'border-r border-gray-200' : ''}`}>
            {activePage === 'chat' && (
              <ChatArea
                activePage={activePage}
                onDocumentClick={handleDocumentSelect}
                sidebarCollapsed={sidebarCollapsed}
                hasOpenDocuments={selectedChatDocuments.length > 0}
              />
            )}

            {activePage === 'documents' && (
              <DocumentsPage
                onDocumentSelect={handleDocumentSelect}
                isDocumentViewerOpen={showDocWindow && activePage === 'documents'}
                onAddDocuments={() => setShowDocumentOnboarding(true)}
              />
            )}

            {activePage === 'uspto_data' && (
              <USPTODataPage onDocumentSelect={handleDocumentSelect} />
            )}

            {activePage === 'examination' && (
              <ExaminationPage
                key={examinationApplicationNumber || 'default'}
                onDocumentSelect={handleDocumentSelect}
                initialApplicationNumber={examinationApplicationNumber}
                initialDocument={selectedDocument}
              />
            )}

            {activePage === 'resources' && (
              <ResourcesPage
                onDocumentSelect={handleDocumentSelect}
              />
            )}

            {activePage === 'tasks' && (
              <TasksPage />
            )}

            {activePage === 'sessions' && (
              <SessionsPage
                onBack={() => setActivePage('chat')}
              />
            )}

            {activePage === 'drafts' && (
              <DraftsPage
                onBack={() => setActivePage('chat')}
              />
            )}
          </div>

          {/* Document / Reference Viewer - hidden on resources page */}
          {(showDocWindow || mpepViewerState || cfrViewerState || uscViewerState || applicationViewerState) && activePage !== 'resources' && (
            <div
              className="relative flex"
              style={{
                width: activePage === 'documents' ? 'auto' : `${documentViewerWidth}px`,
                flex: activePage === 'documents' ? 1 : 'none'
              }}
            >
              {/* Resize Handle - only show on chat page */}
              {activePage === 'chat' && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-1 bg-gray-200 hover:bg-blue-500 cursor-ew-resize transition-colors z-10"
                  onMouseDown={handleResizeStart}
                  style={{
                    backgroundColor: isResizing ? '#3b82f6' : undefined
                  }}
                >
                  {/* Expanded hit area for easier grabbing */}
                  <div className="absolute inset-y-0 -left-2 -right-2" />
                </div>
              )}
              {mpepViewerState ? (
                <MpepViewer
                  section={mpepViewerState.section}
                  title={mpepViewerState.title}
                  content={mpepViewerState.content}
                  loading={mpepViewerState.loading}
                  error={mpepViewerState.error}
                  onClose={handleCloseMpepViewer}
                  onReload={() => loadMpepSection(mpepViewerState.section, mpepViewerState.title)}
                  onSectionSelect={handleNavigateToMpepSection}
                  chapters={mpepStructure}
                  chaptersLoading={mpepStructureLoading}
                />
              ) : cfrViewerState ? (
                <CfrViewer
                  section={cfrViewerState.section}
                  title={cfrViewerState.title}
                  content={cfrViewerState.content}
                  loading={cfrViewerState.loading}
                  error={cfrViewerState.error}
                  onClose={handleCloseCfrViewer}
                  onReload={() => loadCfrSection(cfrViewerState.section, cfrViewerState.title)}
                  onSectionSelect={handleNavigateToCfrSection}
                  structure={cfrStructure || undefined}
                  structureLoading={cfrStructureLoading}
                />
              ) : uscViewerState ? (
                <UscViewer
                  section={uscViewerState.section}
                  title={uscViewerState.title}
                  content={uscViewerState.content}
                  loading={uscViewerState.loading}
                  error={uscViewerState.error}
                  onClose={handleCloseUscViewer}
                  onReload={() => loadUscSection(uscViewerState.section, uscViewerState.title)}
                  onSectionSelect={handleNavigateToUscSection}
                  structure={uscStructure || undefined}
                  structureLoading={uscStructureLoading}
                />
              ) : applicationViewerState ? (
                <div className="w-full h-full bg-white flex flex-col">
                  <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {applicationViewerState.pdfUrl ? 'Prosecution Document' : 'Application Data'}
                    </h2>
                    <div className="flex items-center gap-2">
                      {applicationViewerState.pdfUrl && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setApplicationViewerState(prev => prev ? { ...prev, pdfUrl: null, pdfLoading: false } : null)}
                        >
                          Back to Application
                        </Button>
                      )}
                      <button
                        onClick={handleCloseApplicationViewer}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto">
                    {applicationViewerState.pdfLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Loading prosecution document...</span>
                        </div>
                      </div>
                    ) : applicationViewerState.pdfUrl ? (
                      <div className="flex flex-col items-center p-4 h-full overflow-auto">
                        <div className="mb-4 flex gap-2">
                          <Button size="sm" onClick={() => setPdfScale(Math.max(0.5, pdfScale - 0.1))}>
                            Zoom Out
                          </Button>
                          <Button size="sm" onClick={() => setPdfScale(1.0)}>
                            Reset ({Math.round(pdfScale * 100)}%)
                          </Button>
                          <Button size="sm" onClick={() => setPdfScale(Math.min(2.0, pdfScale + 0.1))}>
                            Zoom In
                          </Button>
                        </div>
                        <div className="w-full max-w-[900px]">
                          <Document
                            file={applicationViewerState.pdfUrl}
                            onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                            onLoadError={(error) => {
                              console.error('Error loading PDF:', error);
                              addNotification({
                                type: 'error',
                                title: 'Error',
                                message: 'Failed to load PDF',
                              });
                            }}
                            loading={
                              <div className="flex items-center justify-center p-8">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                              </div>
                            }
                          >
                            {Array.from(new Array(pdfNumPages), (el, index) => (
                              <Page
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                                scale={pdfScale}
                                className="mb-4 shadow-lg"
                              />
                            ))}
                          </Document>
                        </div>
                      </div>
                    ) : (
                      <USApplicationDisplay
                        applicationData={applicationViewerState.applicationData}
                        onDocumentClick={handleDocumentSelect}
                        onProsecutionDocClick={handleProsecutionDocClick}
                      />
                    )}
                  </div>
                </div>
              ) : (
                showDocWindow && (
                  activePage === 'chat' && selectedChatDocuments.length > 0 ? (
                    // Multi-document view for chat page
                    <div className="h-full flex flex-col bg-white flex-1">
                      {/* Header with document management */}
                      <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between bg-gray-50">
                        <div className="flex items-center gap-3 flex-wrap">
                          <BookOpen className="w-5 h-5 text-purple-600" />
                          <span className="font-semibold text-gray-900">
                            {selectedChatDocuments.length} Document{selectedChatDocuments.length > 1 ? 's' : ''}
                          </span>
                          {selectedChatDocuments.map((chatDoc) => (
                            <div key={chatDoc.id} className="flex items-center gap-1 bg-white border border-gray-300 rounded px-2 py-1">
                              <span className="text-xs truncate max-w-[120px]">
                                {chatDoc.doc.file_name || chatDoc.doc.title || 'Document'}
                              </span>
                              <button
                                onClick={() => removeChatDocument(chatDoc.id)}
                                className="text-gray-500 hover:text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.dispatchEvent(new Event('open-document-panel'))}
                            className="h-7 px-2"
                            title="Add document"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedChatDocuments.length >= 2 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm">
                                  Layout
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => setChatDocumentArrangement('horizontal')}>
                                  Side-by-Side
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setChatDocumentArrangement('vertical')}>
                                  Stacked
                                </DropdownMenuItem>
                                {selectedChatDocuments.length >= 3 && (
                                  <DropdownMenuItem onClick={() => setChatDocumentArrangement('grid')}>
                                    Grid
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={removeAllChatDocuments}
                          >
                            Close All
                          </Button>
                        </div>
                      </div>

                      {/* Documents area */}
                      <div className="flex-1 overflow-hidden">
                        {selectedChatDocuments.length === 1 ? (
                          <DocumentViewer
                            document={selectedChatDocuments[0].doc}
                            onClose={() => removeChatDocument(selectedChatDocuments[0].id)}
                            isFullWidth={false}
                            onViewApplication={handleViewApplicationFromDocument}
                          />
                        ) : chatDocumentArrangement === 'horizontal' ? (
                          <div className="flex h-full">
                            {selectedChatDocuments.map((chatDoc) => (
                              <div key={chatDoc.id} className="flex-1 border-r last:border-r-0 border-gray-200 overflow-hidden">
                                <DocumentViewer
                                  document={chatDoc.doc}
                                  onClose={() => removeChatDocument(chatDoc.id)}
                                  isFullWidth={false}
                                  onViewApplication={handleViewApplicationFromDocument}
                                />
                              </div>
                            ))}
                          </div>
                        ) : chatDocumentArrangement === 'vertical' ? (
                          <div className="flex flex-col h-full">
                            {selectedChatDocuments.map((chatDoc) => (
                              <div key={chatDoc.id} className="flex-1 border-b last:border-b-0 border-gray-200 overflow-hidden">
                                <DocumentViewer
                                  document={chatDoc.doc}
                                  onClose={() => removeChatDocument(chatDoc.id)}
                                  isFullWidth={false}
                                  onViewApplication={handleViewApplicationFromDocument}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-px h-full bg-gray-200">
                            {selectedChatDocuments.map((chatDoc) => (
                              <div key={chatDoc.id} className="bg-white overflow-hidden">
                                <DocumentViewer
                                  document={chatDoc.doc}
                                  onClose={() => removeChatDocument(chatDoc.id)}
                                  isFullWidth={false}
                                  onViewApplication={handleViewApplicationFromDocument}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Single document view for non-chat pages
                    <DocumentViewer
                      document={selectedDocument}
                      onClose={handleCloseDocument}
                      isFullWidth={activePage === 'documents'}
                      onViewApplication={handleViewApplicationFromDocument}
                    />
                  )
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Session Expired Modal */}
      <Dialog open={showSessionExpiredModal} onOpenChange={setShowSessionExpiredModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Session Expired</DialogTitle>
            <DialogDescription className="text-gray-600">
              {sessionExpiredMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleSessionExpiredConfirm} className="w-full sm:w-auto">
              Login Again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications */}
      <Notifications />

      {/* Document Onboarding Modal */}
      {showDocumentOnboarding && (
        <DocumentOnboarding
          onClose={() => setShowDocumentOnboarding(false)}
          contextData={sessionContext || {}}
          sessionApplications={[]}
          onDocumentAdded={() => {
            // TODO: Refresh documents list
            console.log('Document added');
          }}
          onOpenUploadModal={() => {
            setShowDocumentOnboarding(false);
            setShowFileUploadModal(true);
          }}
          onSwitchToBatch={() => {
            setShowDocumentOnboarding(false);
            setShowBatchDocumentOnboarding(true);
          }}
        />
      )}

      {/* Batch Document Onboarding Modal */}
      {showBatchDocumentOnboarding && (
        <BatchDocumentOnboarding
          onClose={() => setShowBatchDocumentOnboarding(false)}
          contextData={sessionContext || {}}
          onDocumentAdded={() => {
            // TODO: Refresh documents list
            console.log('Document added');
          }}
          onSwitchToSingle={() => {
            setShowBatchDocumentOnboarding(false);
            setShowDocumentOnboarding(true);
          }}
        />
      )}

      {/* File Upload Modal */}
      <FileUploadModal
        open={showFileUploadModal}
        onOpenChange={setShowFileUploadModal}
      />

      {/* Save Session Modal */}
      <SaveSessionModal
        open={showSaveModal}
        onOpenChange={setShowSaveModal}
        saveAsMode={saveAsMode}
      />

      {/* New Session Warning Modal */}
      <NewSessionWarningModal
        open={showNewSessionWarning}
        onOpenChange={setShowNewSessionWarning}
        onConfirm={handleNewSessionConfirm}
        onSaveFirst={handleNewSessionSaveFirst}
        hasUnsavedChanges={hasUnsavedChanges}
        hasDocuments={hasDocuments}
        documentCount={getActiveDocumentCount()}
      />

      {/* Onboarding Wizard */}
      <OnboardingWizard
        open={showOnboardingWizard}
        onOpenChange={setShowOnboardingWizard}
        sessionContext={{ ...sessionContext, session_applications: sessionApplications }}
        onPasteQuestion={handlePasteQuestionFromWizard}
        onOpenDocumentModal={handleOpenDocumentModalFromWizard}
        onOpenUploadModal={handleOpenUploadModalFromWizard}
        onNavigateToPage={handleNavigateToPageFromWizard}
        onAddApplication={handleAddApplicationFromWizard}
        onSaveSession={() => setShowSaveModal(true)}
      />
    </div>
  );
}
